/**
 * PropertyIntakeForm — SCRIPT-DRIVEN property intake for the Runner.
 * ──────────────────────────────────────────────────────────────────
 * Sibling of NewClientIntakeForm but writes to an EXISTING property_id
 * (from captured_data.property_id). Field order + question text come from
 * the template's `prompts:` string; metadata from the Overwatch Field
 * Registry (category=property). No hardcoded field list.
 *
 * On Submit: PUT /api/properties/{id} with the collected form values.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Loader2, Save, CheckCircle2, AlertCircle, ExternalLink, Plus } from 'lucide-react';
import { parsePromptScript } from '../intake/NewClientIntakeForm';
import { InlineParamEdit, FieldRowControls, advanceOnEnter } from './scriptEditControls';
import NameTypeahead from './NameTypeahead';
import Combobox from '@/components/Combobox';
import AgentFormModal from '@/components/AgentFormModal';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function useFieldRegistry(category) {
  const [byKey, setByKey] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get('/overwatch/fields', { params: { category } })
      .then(res => {
        if (cancelled) return;
        const map = {};
        (res.data?.fields || []).forEach(f => { map[f.key] = f; });
        setByKey(map);
      })
      .catch(() => setByKey({}));
    return () => { cancelled = true; };
  }, [category]);
  return byKey;
}

function useDropdown(category, cascadeValue) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    const params = cascadeValue ? { parent: cascadeValue } : {};
    api.get(`/dropdowns/${category}`, { params })
      .then(res => {
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : (res.data?.options || res.data?.items || []);
        // 2026-02 Go-Live · roof-colour redesign — the seed carries
        // indented brand names ("   Ebony") so the native <select>
        // visually nests COLORSTEEL® basics under their matching
        // general colour. The LABEL keeps the indent; the stored
        // VALUE is trimmed so filters / reports / the Component engine
        // see clean names. No-op on other dropdowns (no leading ws).
        setOptions(raw.map(o => typeof o === 'string'
          ? { value: o.trim(), label: o }
          : { value: o.value || o.name || o.key, label: o.label || o.name || o.value || o.key }));
      })
      .catch(() => setOptions([]));
    return () => { cancelled = true; };
  }, [category, cascadeValue]);
  return { options };
}

/**
 * AgentPickerRow — the Managing-Agent cluster used inside the scripted
 * intake for the `real_estate_agent_id` field. Mirrors the UX on the
 * Property Detail page's "Pick existing agent" card:
 *   • Type-ahead Combobox of active agents (label = `AGENT# — Name · Company`)
 *   • `[+ New Agent]` pill button that opens the shared AgentFormModal
 *   • On pick or new-save → auto-fill sibling fields (agent_name /
 *     agent_company / agent_phone / agent_email) via `onAutoFill`
 *   • Small "Linked to AGENT# · Edit this agent" hint below the picker
 */
function AgentPickerRow({ value, onChange, onAutoFill, testid }) {
  const [agents, setAgents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editAgent, setEditAgent] = useState(null);

  const loadAgents = () => {
    api.get('/agents').then(res => {
      const rows = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.agents || []);
      setAgents(rows);
    }).catch(() => setAgents([]));
  };
  useEffect(() => { loadAgents(); }, []);

  const activeAgents = agents.filter(a => a.active !== false);
  const linked = agents.find(a => a._id === value) || null;
  const displayLabel = linked ? `${linked.agent_no} — ${linked.name || ''} · ${linked.company || ''}` : '';

  const applyAgent = (a) => {
    onChange(a._id);
    if (onAutoFill) {
      onAutoFill({
        agent_name: a.name || '',
        agent_company: a.company || '',
        agent_phone: a.phone || '',
        agent_email: a.email || '',
      });
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <Combobox
            testId={testid}
            value={displayLabel}
            onChange={(label) => {
              const a = activeAgents.find(x => label && label.startsWith(x.agent_no));
              if (a) applyAgent(a);
              else if (!label) { onChange(null); }
            }}
            options={activeAgents.map(a => `${a.agent_no} — ${a.name || ''} · ${a.company || ''}`)}
            placeholder="Search by company or name…"
          />
        </div>
        <button
          type="button"
          data-testid={`${testid}-new-btn`}
          onClick={() => { setEditAgent(null); setShowModal(true); }}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-emerald-500 text-emerald-700 rounded-md text-sm font-semibold hover:bg-emerald-50 whitespace-nowrap"
        >
          <Plus size={14} strokeWidth={2.5} /> New Agent
        </button>
      </div>
      {linked && (
        <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500">
          <span>Linked to <span className="font-mono font-semibold text-emerald-700">{linked.agent_no}</span></span>
          <button
            type="button"
            data-testid={`${testid}-edit-link`}
            onClick={() => { setEditAgent(linked); setShowModal(true); }}
            className="underline hover:text-emerald-700"
          >
            Edit this agent
          </button>
        </div>
      )}
      <AgentFormModal
        open={showModal}
        agent={editAgent}
        prefillFromClient={null}
        onClose={() => { setShowModal(false); setEditAgent(null); }}
        onSaved={(saved) => {
          setShowModal(false); setEditAgent(null);
          loadAgents();
          if (saved && saved._id) applyAgent(saved);
        }}
      />
    </div>
  );
}


/**
 * ConcernListInput — repeatable-row control for `type: concern_list` fields.
 * Each row = Type dropdown + Location dropdown + Notes text. Add / Remove
 * bounded by `field.max_rows` (default 5). Persists as a JSON array.
 * Empty rows are filtered out at save time in PropertyIntakeForm.submit().
 */
function ConcernListInput({ field, value, onChange, testid }) {
  const { options: typeOpts }     = useDropdown(field.concern_type_dropdown);
  const { options: locationOpts } = useDropdown(field.concern_location_dropdown);
  const rows = Array.isArray(value) ? value : [];
  const maxRows = field.max_rows || 5;

  // iter303aj-fix5 — When `rows` is empty we render a phantom placeholder
  // row so operators see an editable form. But mutations against the
  // phantom must clone from `list` (phantom-inclusive) instead of `rows`
  // (real state) — otherwise `rows.map` on an empty array returns `[]`
  // and the first selection is silently dropped.
  const list = rows.length ? rows : [{ type: '', location: '', notes: '' }];

  const setRow = (i, patch) => {
    const next = list.map((r, idx) => idx === i ? { ...r, ...patch } : r);
    onChange(next);
  };
  const addRow = () => {
    if (list.length >= maxRows) return;
    onChange([...list, { type: '', location: '', notes: '' }]);
  };
  const removeRow = (i) => {
    if (!rows.length) return;             // nothing real to remove from the phantom
    onChange(rows.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      {list.map((row, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-start p-2 bg-slate-50 border border-slate-200 rounded-md">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Type</div>
            <select value={row.type || ''} onChange={e => setRow(i, { type: e.target.value })}
              data-testid={`${testid}-row-${i}-type`}
              className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-sky-300">
              <option value="">— pick —</option>
              {typeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Location</div>
            <select value={row.location || ''} onChange={e => setRow(i, { location: e.target.value })}
              data-testid={`${testid}-row-${i}-location`}
              className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-sky-300">
              <option value="">— pick —</option>
              {locationOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Notes</div>
            <input value={row.notes || ''} onChange={e => setRow(i, { notes: e.target.value })}
              data-testid={`${testid}-row-${i}-notes`}
              placeholder="Extra detail (optional)"
              className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-sky-300" />
          </div>
          <button type="button" onClick={() => removeRow(i)}
            data-testid={`${testid}-row-${i}-remove`}
            title="Remove this row"
            className="self-end text-slate-400 hover:text-rose-500 text-lg leading-none px-2 py-1">
            ×
          </button>
        </div>
      ))}
      <div className="flex items-center gap-3 pt-1">
        <button type="button" onClick={addRow}
          disabled={list.length >= maxRows}
          data-testid={`${testid}-add`}
          className="text-xs font-semibold px-3 py-1.5 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed">
          + Add another concern
        </button>
        <span className="text-[11px] text-slate-400">{list.length} of {maxRows} row{maxRows === 1 ? '' : 's'}</span>
      </div>
    </div>
  );
}


function FieldInput({ field, value, onChange, form, testid }) {
  const cascadeVal = field.cascade_from ? form[field.cascade_from] : null;
  const { options } = useDropdown(field.dropdown, cascadeVal);
  if (field.type === 'concern_list') {
    return <ConcernListInput field={field} value={value} onChange={onChange} testid={testid} />;
  }  if (field.type === 'multi-dropdown') {
    const selected = Array.isArray(value) ? value : (value ? String(value).split(',').map(s => s.trim()).filter(Boolean) : []);
    const toggle = (opt) => {
      const has = selected.includes(opt);
      onChange(has ? selected.filter(x => x !== opt) : [...selected, opt]);
    };
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map(o => {
          const isOn = selected.includes(o.value);
          return (
            <button key={o.value} type="button" onClick={() => toggle(o.value)}
              data-testid={`${testid}-${o.value}`}
              className={`px-3 py-1.5 text-sm rounded-full border transition
                ${isOn
                  ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
                  : 'border-slate-300 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50'}`}>
              {isOn && '✓ '}{o.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (field.type === 'yes_no') {
    const isYes = value === true || value === 'yes' || value === 'true';
    const isNo = value === false || value === 'no' || value === 'false';
    return (
      <div className="flex gap-2" data-testid={testid}>
        <button type="button" onClick={() => onChange(true)}
          data-testid={`${testid}-yes`}
          className={`px-4 py-2 text-sm font-semibold rounded-lg border-2 transition
            ${isYes
              ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
              : 'border-slate-300 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50'}`}>
          {isYes && '✓ '}Yes
        </button>
        <button type="button" onClick={() => onChange(false)}
          data-testid={`${testid}-no`}
          className={`px-4 py-2 text-sm font-semibold rounded-lg border-2 transition
            ${isNo
              ? 'border-slate-500 bg-slate-500 text-white shadow-sm'
              : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50'}`}>
          {isNo && '✓ '}No
        </button>
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
          data-testid={testid} className="w-4 h-4" />
        <span className="text-sm text-slate-600">Yes</span>
      </label>
    );
  }
  if (field.type === 'textarea') {
    return (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)}
        data-testid={testid} rows={2}
        className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300" />
    );
  }
  if (field.type === 'number') {
    return (
      <input type="number" value={value ?? ''} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300" />
    );
  }
  if (field.type === 'dropdown') {
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full text-sm px-3 py-2 border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300">
        <option value="">— choose —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.type === 'dropdown-searchable') {
    return (
      <div>
        <input list={`dl-${field.key}`} value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={cascadeVal ? `Filtered by ${cascadeVal}` : 'Type to filter…'}
          data-testid={testid}
          className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300" />
        <datalist id={`dl-${field.key}`}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </datalist>
      </div>
    );
  }
  if (field.type === 'typeahead') {
    return (
      <NameTypeahead
        value={value || ''}
        onChange={onChange}
        suggestionsSource={field.suggestions_source || field.key}
        placeholder={field.placeholder || 'Type to search or add new…'}
        testId={testid}
      />
    );
  }
  return (
    <input type={field.type} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder || ''} data-testid={testid}
      className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300" />
  );
}

export default function PropertyIntakeForm({ propertyId, initial, prompts, onSave, onCancel, autoSaveDraft, editCtx, extraPatch, hideAnswered = false }) {
  const registry = useFieldRegistry('property');
  const script = useMemo(() => parsePromptScript(prompts), [prompts]);
  const [form, setForm] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const autosaveTimer = useRef(null);
  // iter303ak-fix6 · Local "reveal answered" toggle so operators can pop
  // the summary card open if they need to correct something the caller
  // pre-filled incorrectly. Default: honour the hideAnswered prop.
  const [showAnswered, setShowAnswered] = useState(!hideAnswered);
  // Snapshot of the form values captured on mount — used by the Discard
  // edits link (edit mode) to restore what the operator had before opening
  // Edit answered fields. Also used to count how many fields the operator
  // actually changed during an edit pass (drives the "✓ N fields updated"
  // banner shown after Save Updates flips back to summary view).
  const initialFormRef = useRef(null);
  const [justUpdatedCount, setJustUpdatedCount] = useState(0);
  // iter303ak-fix10 · Trevor: "only allowing me to enter 1 keyboard entry
  // then it automatically refreshes" — the answered/unanswered split
  // re-computed on every keystroke, so as soon as a number field like
  // floor_m2 went from 0 → "1", isAnswered flipped and the input jumped
  // out of the visible unanswered list mid-typing. Freeze the split
  // behind an explicit refresh tick: bumped on mount, when `initial`
  // arrives from the parent, on manual "Refresh missing fields" click,
  // and immediately before Submit / Continue. Field values still update
  // in real-time; only the "which fields are shown / hidden" partition
  // stays stable until the operator asks for a recheck.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    // Snapshot once per action mount (not on every keystroke).
    if (initialFormRef.current === null) initialFormRef.current = { ...form };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter303ag-fix2 — Merge late-arriving initial fields into form.
  // `initial` may update after the first mount when the parent
  // (OpenPropertyIntakeForm) fetches the roof_details record. Any user
  // edits already in `form` win; empty/undefined slots get populated
  // from the new initial. Runs only when `initial`'s identity changes
  // (memoized upstream on a content signature), never on every render.
  useEffect(() => {
    setForm(prev => {
      const merged = { ...prev };
      Object.entries(initial || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        const cur = merged[k];
        if (cur === undefined || cur === null || cur === '') {
          merged[k] = v;
        }
      });
      return merged;
    });
    // iter303ak-fix10 — refresh the answered/unanswered partition whenever
    // late-arriving initial data lands, so it reflects the real baseline.
    setRefreshTick(t => t + 1);
  }, [initial]);

  const fields = useMemo(() => {
    return script
      .map(({ key, prompt }) => {
        const meta = registry[key];
        if (!meta) return null;
        return { ...meta, prompt: prompt || meta.default_prompt || '' };
      })
      .filter(Boolean);
  }, [script, registry]);

  // iter303ak-fix2 — Smart "hide answered" mode. Splits fields into two
  // buckets: answered (has a non-empty value in the current form) vs
  // unanswered. Rendered as a collapsible summary card at the top +
  // active prompts for the gaps only. When hideAnswered=false the
  // component behaves exactly like before (all fields render).
  const isAnswered = (key) => {
    const v = form[key];
    if (v === undefined || v === null) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    // iter303ak-fix9 · Numeric 0 shouldn't count as an "answer" for
    // number-type fields — the backend `RoofDetailInput` model defaults
    // every number (roof_m2, floor_m2, ridge_length, barge_length,
    // perimeter_length, roof_angle) to 0 at record creation, and
    // operators expect those to still be prompted during the on-site
    // refresh. Trevor: "these values are '0' which don't count as a value".
    if (registry[key]?.type === 'number' && Number(v) === 0) return false;
    return true;
  };
  const answeredFields = useMemo(
    () => hideAnswered ? fields.filter(f => isAnswered(f.key)) : [],
    // iter303ak-fix10 — freeze the split on `refreshTick` (not on every
    // `form` change) so typing into a numeric field doesn't yank it out
    // of the visible unanswered list mid-keystroke. Bumped by initial-
    // hydration, the Refresh button, and Submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hideAnswered, fields, refreshTick]
  );
  const unansweredFields = useMemo(
    () => hideAnswered ? fields.filter(f => !isAnswered(f.key)) : fields,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hideAnswered, fields, refreshTick]
  );

  // Count how many follow-up fields cascade off each field in this form
  // (i.e. their `show_if` points at this key). Powers the "→ reveals N more
  // questions" hint on Yes/No radio hubs like tenant_has_special_requirements.
  const cascadeCounts = useMemo(() => {
    const counts = {};
    fields.forEach(f => {
      if (!f.show_if) return;
      const key = f.show_if.startsWith('!') ? f.show_if.slice(1) : f.show_if;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [fields]);

  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (autoSaveDraft) { autoSaveDraft(form); setSavedAt(new Date()); }
    }, 3000);
    return () => clearTimeout(autosaveTimer.current);
  }, [form, autoSaveDraft]);

  // iter303ak-fix9 · Auto-calculate Roof m² from Floor m² × sec(angle).
  // Mirrors the formula on the standalone Roof Details form (RoofForm.js).
  // Only fires when both inputs are valid (>0, angle<80°) and never
  // clobbers a value the operator entered manually more than 0.01 away
  // from the estimate.
  useEffect(() => {
    const f = parseFloat(form.floor_m2);
    const a = parseFloat(form.roof_angle);
    if (!(f > 0 && a > 0 && a < 80)) return;
    const est = Math.round((f / Math.cos((a * Math.PI) / 180)) * 100) / 100;
    if (Math.abs((parseFloat(form.roof_m2) || 0) - est) > 0.01) {
      setForm(prev => ({ ...prev, roof_m2: est }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.floor_m2, form.roof_angle]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Format an axios error into a readable string. Pydantic 422 returns an
  // ARRAY of {type, loc, msg, input, url} objects on `detail`; naively
  // setState'ing that array crashes React with "Objects are not valid as a
  // React child". Extract per-field messages joined with newlines instead.
  const formatError = (e) => {
    const detail = e?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail
        .map(d => {
          const loc = Array.isArray(d?.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
          return loc ? `${loc}: ${d?.msg || 'invalid'}` : (d?.msg || String(d));
        })
        .join('\n');
    }
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') return JSON.stringify(detail);
    return e?.message || 'Save failed';
  };

  // Coerce values to the shape the backend expects. Certain fields are
  // stored as ints on the Pydantic model but the picker returns strings
  // (e.g. `roof_stories` returns "1 Storey" / "2 Storey" / "3+ Storey").
  // Strip the leading integer so Pydantic accepts it. Empty strings for
  // number fields are dropped so defaults aren't clobbered.
  const coerceValue = (field, val) => {
    if (val === '' || val === null || val === undefined) return undefined;
    if (field.type === 'concern_list') {
      // Filter out completely empty rows (no type + no location + no notes)
      const rows = Array.isArray(val) ? val : [];
      const cleaned = rows.filter(r => (r.type || r.location || r.notes || '').toString().trim());
      return cleaned;
    }
    if (field.key === 'roof_stories') {
      const m = String(val).match(/(\d+)/);
      return m ? parseInt(m[1], 10) : undefined;
    }
    if (field.type === 'number') {
      const n = Number(val);
      return Number.isFinite(n) ? n : undefined;
    }
    if (field.type === 'yes_no') {
      // Normalise to boolean for Pydantic bool fields
      if (val === true || val === 'yes' || val === 'true') return true;
      if (val === false || val === 'no' || val === 'false') return false;
      return undefined;
    }
    return val;
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    // iter303ak-fix10 — Trevor: "when I press Continue, it refreshes to
    // confirm and see what is left". Bump the tick so the summary card
    // + missing-fields list re-partition against the latest form values
    // before we save + potentially advance.
    setRefreshTick(t => t + 1);
    if (!propertyId) { setError('Missing property_id in captured_data — cannot save.'); return; }
    setSaving(true);
    try {
      // Split form values by their registry `entity` — property fields go to
      // PUT /properties/{id}; roof fields go to POST/PUT /roof-details
      // against the property's primary roof record (created if missing).
      //
      // Iter303ag — PUT /properties/{id} uses the full PropertyInput model;
      // any field omitted from the body is reset to its Pydantic default
      // (empty string / false). To prevent that from clobbering existing
      // data we send the CURRENT property record (via `initial`) as the
      // base and layer our coerced updates + extraPatch on top. Pydantic
      // silently ignores extra keys (like _id, property_no, created_at).
      const propertyPatch = { ...(initial || {}), ...(extraPatch || {}) };
      const roofPatch = {};
      // Strip anything undefined / null / empty-string from the base so we
      // don't accidentally re-post junk. Real user edits will be layered on
      // top in the coerce loop below.
      Object.keys(propertyPatch).forEach(k => {
        const v = propertyPatch[k];
        if (v === null || v === undefined) delete propertyPatch[k];
      });
      fields.forEach(f => {
        const raw = form[f.key];
        const val = coerceValue(f, raw);
        // iter303ak-fix8 · Distinguish "explicit clear" (raw === '' or null)
        // from "not touched" (raw === undefined) so operator-cleared fields
        // ACTUALLY clear on the PATCH instead of falling back to `initial`.
        // Before: clearing roof_height showed as saved but reverted to 1
        // because coerceValue returned undefined and the initial-merge
        // preserved the previous value.
        const explicitlyCleared = raw === '' || raw === null;
        if (f.entity === 'roof') {
          delete propertyPatch[f.key];    // never leak roof fields to property PUT
          if (val !== undefined) roofPatch[f.key] = val;
          else if (explicitlyCleared) roofPatch[f.key] = f.type === 'number' ? null : '';
        } else {
          if (val !== undefined) propertyPatch[f.key] = val;
          else if (explicitlyCleared) propertyPatch[f.key] = f.type === 'number' ? null : '';
        }
      });

      // 1) Property fields
      let propertyRecord = null;
      if (Object.keys(propertyPatch).length) {
        const res = await api.put(`/properties/${propertyId}`, propertyPatch);
        propertyRecord = res.data;
      }

      // 2) Roof fields — find or create the primary roof record
      let roofRecord = null;
      if (Object.keys(roofPatch).length) {
        const existing = await api.get(`/roof-details/${propertyId}`).then(r => (r.data || [])[0]).catch(() => null);
        if (existing?._id) {
          const res = await api.put(`/roof-details/${existing._id}`, { ...existing, ...roofPatch });
          roofRecord = res.data;
        } else {
          const body = { property_id: propertyId, description: 'Main Roof', ...roofPatch };
          const res = await api.post('/roof-details', body);
          roofRecord = res.data;
        }
      }

      // iter303ak-fix6 · Re-confirmation gate.
      //
      // If the operator is in EDIT mode (hideAnswered=true &&
      // showAnswered=true), they explicitly clicked "Save Updates" — save
      // + flip back to summary + show the "N updated" banner. Do NOT
      // advance the runner — they need to eyeball the refreshed summary
      // and click Confirm & Continue.
      //
      // If they're in SUMMARY view or the form isn't in smart-mode at all,
      // "Save & Continue" fires the normal advance via onSave().
      if (hideAnswered && showAnswered) {
        // Diff the changed count against the mount snapshot so the banner
        // has a concrete number.
        const before = initialFormRef.current || {};
        const changed = fields.filter(f => {
          const bv = before[f.key];
          const av = form[f.key];
          const strA = av === null || av === undefined ? '' : String(av).trim();
          const strB = bv === null || bv === undefined ? '' : String(bv).trim();
          return strA !== strB;
        }).length;
        setJustUpdatedCount(changed);
        // Reset the snapshot so subsequent edit passes count fresh.
        initialFormRef.current = { ...form };
        setShowAnswered(false);           // flip back to summary card
        setSavedAt(Date.now());
        // iter303ak-fix13 · Trevor: "Save updates returned to summary
        // and details are not refreshed automatically." Bump refreshTick
        // so the answered/unanswered partition re-computes against the
        // just-saved form state. Without this the summary still shows
        // the pre-edit split.
        setRefreshTick(t => t + 1);
        // Do not advance — operator must click Confirm & Continue.
        return;
      }

      onSave({ property_id: propertyId, property_record: propertyRecord, roof_record: roofRecord, form });
    } catch (e) {
      setError(formatError(e));
    } finally { setSaving(false); }
  };

  // iter303ak-fix6 · Discard-edits inside the edit banner. Restores the
  // form state to the mount snapshot (BEFORE any edits in this pass) and
  // flips back to the summary card. Outer Cancel button remains as the
  // way to abandon the whole action.
  const discardEdits = () => {
    if (initialFormRef.current) {
      setForm({ ...initialFormRef.current });
    }
    setShowAnswered(false);
    setJustUpdatedCount(0);
  };

  return (
    <form onSubmit={submit} onKeyDown={advanceOnEnter} className="space-y-5">
      <div className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500 rounded-r-lg p-4 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1 flex items-center gap-2">
            <span>🏠</span>
            <InlineParamEdit
              editCtx={editCtx}
              paramKey="header_title"
              value={editCtx?.headerTitle || 'Property Details Intake'}
              placeholder="Header title"
              className=""
              dataTestidPrefix="prop-intake-header-title"
              alwaysShowPencil
            />
          </div>
          <div className="text-slate-700 text-sm">
            <InlineParamEdit
              editCtx={editCtx}
              paramKey="header_description"
              value={editCtx?.headerDescription || 'Walk through the questions — read each prompt to the caller and capture the answer. Confirms and updates the property record we just created.'}
              placeholder="Description script"
              className=""
              dataTestidPrefix="prop-intake-header-description"
              multiline
              alwaysShowPencil
            />
          </div>
        </div>
        {propertyId && (
          <a href={`/properties/${propertyId}`} target="_blank" rel="noopener noreferrer"
            data-testid="prop-intake-open-btn"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white border border-emerald-300 rounded hover:bg-emerald-50">
            <ExternalLink size={12} /> Open Full Property Page
          </a>
        )}
      </div>

      {fields.length === 0 && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          No fields in this intake yet. Edit the workflow template&apos;s <code>prompts:</code> list to add fields.
        </div>
      )}

      {/* iter303ak-fix2 · Smart "hide answered" summary card. Shows all
          fields that already have values so the operator sees the state
          at-a-glance without answering them again. Only active prompts
          below are the gaps. Toggle to reveal + edit answered items.
          iter303ak-fix6 · adds Discard-edits link (edit mode) + green
          "N updated" flash banner after Save Updates flips back to summary. */}
      {hideAnswered && answeredFields.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4" data-testid="prop-intake-answered-summary">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-600" />
              <div className="text-sm font-semibold text-emerald-900">
                {answeredFields.length} field{answeredFields.length === 1 ? '' : 's'} already captured
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* iter303ak-fix10 — Manual "recheck now" trigger. The
                  answered/unanswered partition is frozen behind
                  refreshTick so typing doesn't yank the field out of the
                  list mid-keystroke; this button + Continue re-partition
                  on demand. */}
              <button
                type="button"
                onClick={() => setRefreshTick(t => t + 1)}
                className="text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline"
                data-testid="prop-intake-refresh-missing"
                title="Re-check the answered / missing split against the latest field values"
              >
                Refresh
              </button>
              {showAnswered && (
                <button
                  type="button"
                  onClick={discardEdits}
                  className="text-xs font-medium text-rose-600 hover:text-rose-800 hover:underline"
                  data-testid="prop-intake-discard-edits"
                  title="Restore the values before you opened Edit"
                >
                  Discard edits
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowAnswered(v => !v)}
                className="text-xs font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
                data-testid="prop-intake-toggle-answered"
              >
                {showAnswered ? 'Hide answered fields' : 'Edit answered fields'}
              </button>
            </div>
          </div>
          {/* iter303ak-fix6 · Flash banner after Save Updates */}
          {!showAnswered && justUpdatedCount > 0 && (
            <div className="mb-2 px-3 py-2 rounded bg-emerald-100 border border-emerald-300 text-sm text-emerald-900 font-medium" data-testid="prop-intake-just-updated-banner">
              ✓ {justUpdatedCount} field{justUpdatedCount === 1 ? '' : 's'} updated — review the summary, then click <b>Confirm &amp; Continue</b> below.
            </div>
          )}
          {!showAnswered && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-emerald-800">
              {answeredFields.map(f => (
                <div key={f.key} className="flex items-baseline gap-1">
                  <span className="text-emerald-500">•</span>
                  <span className="font-medium">{f.label}:</span>
                  <span className="truncate">{String(form[f.key])}</span>
                </div>
              ))}
            </div>
          )}
          {unansweredFields.length > 0 && !showAnswered && (
            <div className="mt-3 pt-3 border-t border-emerald-300/60">
              {/* iter303ak-fix8 · Explicit list of still-to-fill fields so
                  operator sees what's missing at-a-glance, not just a
                  "N fields still to answer" one-liner. */}
              <div className="text-xs font-semibold text-rose-700 mb-1 flex items-center gap-1">
                <AlertCircle size={12} />
                {unansweredFields.length} field{unansweredFields.length === 1 ? '' : 's'} still to fill:
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-rose-800">
                {unansweredFields.map(f => (
                  <div key={f.key} className="flex items-baseline gap-1">
                    <span className="text-rose-400">◦</span>
                    <span>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {unansweredFields.length === 0 && !showAnswered && (
            <div className="text-xs text-emerald-700 mt-2 font-medium">
              ✓ Every field is answered — press {justUpdatedCount > 0 ? 'Confirm & Continue' : 'Save & Continue'} below.
            </div>
          )}
        </div>
      )}

      {(hideAnswered && showAnswered ? fields : unansweredFields).map((f, idx) => {
        if (f.show_if) {
          const inv = f.show_if.startsWith('!');
          const key = inv ? f.show_if.slice(1) : f.show_if;
          const on = !!form[key];
          if (inv ? on : !on) return null;
        }
        const cascadeN = cascadeCounts[f.key] || 0;
        const isYesNoHub = f.type === 'yes_no' && cascadeN > 0;
        const isOn = form[f.key] === true || form[f.key] === 'yes' || form[f.key] === 'true';
        return (
        <div key={f.key} className="group border-l-2 border-slate-100 pl-4 relative">
          {f.prompt && (
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="text-sm text-emerald-700 italic" data-testid={`prop-intake-prompt-${f.key}`}>
                💬 &quot;{f.prompt}&quot;
              </div>
              <FieldRowControls
                editCtx={editCtx}
                items={script}
                idx={idx}
                currentPrompt={f.prompt}
              />
            </div>
          )}
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
            {f.required ? '🔒 ' : ''}{f.label}{f.required ? ' *' : ''}
            {isYesNoHub && (
              <span className="ml-2 text-[10px] normal-case tracking-normal text-slate-400 font-normal">
                → Yes reveals {cascadeN} follow-up question{cascadeN === 1 ? '' : 's'}
              </span>
            )}
          </label>
          {f.key === 'real_estate_agent_id' ? (
            <AgentPickerRow
              value={form[f.key] || null}
              onChange={v => setField(f.key, v)}
              onAutoFill={(patch) => setForm(prev => ({ ...prev, ...patch }))}
              testid={`prop-intake-input-${f.key}`}
            />
          ) : (
            <FieldInput field={f} value={form[f.key]} onChange={v => setField(f.key, v)}
              form={form} testid={`prop-intake-input-${f.key}`} />
          )}
          {isYesNoHub && isOn && (
            <div className="mt-1 text-[11px] text-emerald-600 italic">
              ✓ Follow-up questions below are now active
            </div>
          )}
        </div>
      );
      })}

      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 whitespace-pre-wrap">{error}</div>}

      <div className="flex items-center gap-3 sticky bottom-0 bg-white py-3 border-t border-slate-100">
        <div className="text-xs text-slate-400">
          {savedAt && (
            <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /> Draft auto-saved {Math.max(1, Math.round((Date.now() - savedAt) / 1000))}s ago</span>
          )}
        </div>
        <span className="flex-1" />
        <button type="button" onClick={onCancel} data-testid="prop-intake-cancel-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded">
          Cancel
        </button>
        <div className="flex flex-col items-end">
          <button type="submit" disabled={saving} data-testid="prop-intake-save-btn"
            className="px-6 py-2 bg-emerald-600 text-white font-semibold rounded hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {/* iter303ak-fix6 · Label swaps based on mode:
                - Edit view: "Save Updates" (persists + flips to summary + no advance)
                - Summary view after edits: "Confirm & Continue" (advances)
                - Standard: "Continue" (unchanged behaviour) */}
            {hideAnswered && showAnswered
              ? 'Save Updates'
              : (hideAnswered && justUpdatedCount > 0
                  ? 'Confirm & Continue'
                  : 'Continue')}
          </button>
          <span className="text-[10px] text-slate-400 mt-1">
            {hideAnswered && showAnswered
              ? 'saves edits + flips back to summary'
              : 'saves &amp; updates the property record'}
          </span>
        </div>
      </div>
    </form>
  );
}
